import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusPill } from "@/components/PortalShell";
import PhotoUploader from "@/components/PhotoUploader";
import { Download, FileText, Trash2, Plus, HeartPulse } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtD = (d) => { const dt = new Date(d); return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }); };

const STATUS_TINT = {
  "Good": { bg: "#e7ecdf", fg: "#5B6440" }, "Monitor": { bg: "#f2ecd9", fg: "#7a6a2f" },
  "Needs attention": { bg: "#f3e4cf", fg: "#8a5a1f" }, "Urgent": { bg: "#f4ddd9", fg: "#a13529" },
  "Not applicable": { bg: "#eceae6", fg: "#726A5C" },
};

export function HealthCheckPanel({ pid, reload }) {
  const [meta, setMeta] = useState(null);
  const [list, setList] = useState([]);
  const [mode, setMode] = useState("list");

  const loadList = useCallback(() => { if (pid) api.get(`/health-checks?property_id=${pid}`).then(({ data }) => setList(data)).catch(() => {}); }, [pid]);
  useEffect(() => { api.get("/health-check/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { loadList(); }, [loadList]);

  if (mode === "new") return <HealthCheckForm pid={pid} meta={meta} onDone={() => { setMode("list"); loadList(); reload && reload(); }} onCancel={() => setMode("list")} />;

  return (
    <div style={{ display: "grid", gap: 20 }} data-testid="health-check-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <p style={{ color: "var(--muted)", margin: 0, maxWidth: 560, fontSize: 14 }}>A free, non-intrusive property health check to win and onboard clients. Once submitted it appears in the owner's portal.</p>
        <div style={{ display: "flex", gap: 10 }}>
          <a className="btn-outline" href={`${BACKEND}/api/health-check-guide/pdf`} target="_blank" rel="noreferrer" data-testid="health-guide-btn" style={{ padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}><FileText size={15} /> Staff guide</a>
          <button className="btn-ink" onClick={() => setMode("new")} data-testid="new-health-check-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={15} /> New health check</button>
        </div>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {list.length === 0 && <p style={{ color: "var(--muted)", fontSize: 13 }}>No health checks yet.</p>}
        {list.map((h) => {
          const flagged = (h.checklist || []).filter((c) => c.status === "Needs attention" || c.status === "Urgent").length;
          const refs = (h.checklist || []).filter((c) => c.needs_contractor).length;
          return (
            <div key={h.health_check_id} className="card-soft" style={{ padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="health-check-row">
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <HeartPulse size={16} style={{ color: "var(--honey)" }} />
                  <b>{h.visit_date ? fmtD(h.visit_date) : fmtD(h.created_at)}</b>
                  <StatusPill status={h.status === "submitted" ? "Sent to owner" : "Draft"} />
                </div>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{h.condition || "Good"} · {flagged} flagged · {refs} contractor referral{refs === 1 ? "" : "s"} · {h.author}</div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {h.status === "submitted" && <a className="link-u" href={`${BACKEND}/api/health-checks/${h.health_check_id}/pdf`} target="_blank" rel="noreferrer" data-testid="health-pdf-btn" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "none" }}><Download size={14} /> PDF</a>}
                {h.status !== "submitted" && <button className="btn-ink" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => submit(h)} data-testid="submit-health-check-btn">Submit to owner</button>}
                <button className="cal-nav" onClick={() => del(h)} aria-label="Delete" data-testid="delete-health-check-btn" style={{ padding: 6, cursor: "pointer" }}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  async function submit(h) {
    if (!window.confirm("Submit this health check? It becomes visible in the owner's portal and any contractor-flagged items are raised as issues.")) return;
    try { await api.post(`/health-checks/${h.health_check_id}/submit`); toast.success("Health check submitted to owner"); loadList(); reload && reload(); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function del(h) {
    if (!window.confirm("Delete this health check?")) return;
    try { await api.delete(`/health-checks/${h.health_check_id}`); toast.success("Deleted"); loadList(); }
    catch (e) { toast.error(apiErr(e)); }
  }
}

function HealthCheckForm({ pid, meta, onDone, onCancel }) {
  const [f, setF] = useState({ visit_date: new Date().toISOString().slice(0, 10), condition: "Good", summary: "", recommendations: "" });
  const [check, setCheck] = useState({});
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const key = (g, a) => `${g}::${a}`;
  const setItem = (g, a, patch) => setCheck((c) => ({ ...c, [key(g, a)]: { status: "Good", note: "", needs_contractor: false, trade: "", ...(c[key(g, a)] || {}), ...patch } }));
  const get = (g, a) => check[key(g, a)] || { status: "Good", note: "", needs_contractor: false, trade: "" };

  if (!meta) return <p style={{ color: "var(--muted)" }}>Loading…</p>;

  const save = async (submitAfter) => {
    setBusy(true);
    const checklist = [];
    meta.template.forEach(({ group, items }) => items.forEach((area) => {
      const v = get(group, area);
      checklist.push({ group, area, status: v.status, note: v.note, needs_contractor: !!v.needs_contractor, trade: v.trade || "" });
    }));
    try {
      const { data } = await api.post("/health-checks", { property_id: pid, ...f, checklist, photos });
      if (submitAfter) await api.post(`/health-checks/${data.health_check_id}/submit`);
      toast.success(submitAfter ? "Health check submitted to owner" : "Health check saved as draft");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="health-check-form">
      <button className="link-u" onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", justifySelf: "start" }}>← Back to health checks</button>
      <div className="card-soft" style={{ padding: 22, display: "grid", gap: 12 }}>
        <div className="lbl">New health check</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div><div className="lbl-m" style={{ marginBottom: 4 }}>Visit date</div><input className="inp" type="date" value={f.visit_date} onChange={(e) => setF({ ...f, visit_date: e.target.value })} data-testid="health-date" /></div>
          <div style={{ flex: 1, minWidth: 180 }}><div className="lbl-m" style={{ marginBottom: 4 }}>Overall condition</div><select className="inp" value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} data-testid="health-condition">{["Excellent", "Good", "Fair", "Needs attention"].map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>
        <textarea className="inp" rows={3} placeholder="Summary of the visit" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="health-summary" />
        <textarea className="inp" rows={2} placeholder="Recommendations (optional)" value={f.recommendations} onChange={(e) => setF({ ...f, recommendations: e.target.value })} />
      </div>

      {meta.template.map(({ group, items }) => (
        <div key={group} className="card-soft" style={{ padding: 20 }}>
          <div className="lbl-m" style={{ color: "var(--honey)", marginBottom: 10 }}>{group}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {items.map((area) => {
              const v = get(group, area);
              const flag = v.needs_contractor;
              return (
                <div key={area} style={{ display: "grid", gap: 6 }}>
                  <div className="check-row" style={{ display: "grid", gridTemplateColumns: "1.1fr 150px 1.4fr", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 13 }}>{area}</span>
                    <select className="inp" value={v.status} onChange={(e) => setItem(group, area, { status: e.target.value })} data-testid={`hc-status-${group}-${area}`}>{meta.statuses.map((s) => <option key={s}>{s}</option>)}</select>
                    <input className="inp check-note" placeholder="Note (optional)" value={v.note} onChange={(e) => setItem(group, area, { note: e.target.value })} />
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", paddingLeft: 2 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: flag ? "var(--honey)" : "var(--muted)", cursor: "pointer" }}>
                      <input type="checkbox" checked={flag} onChange={(e) => setItem(group, area, { needs_contractor: e.target.checked })} data-testid={`hc-contractor-${group}-${area}`} /> Contractor recommended
                    </label>
                    {flag && <select className="inp" style={{ maxWidth: 200, padding: "4px 8px", fontSize: 12 }} value={v.trade} onChange={(e) => setItem(group, area, { trade: e.target.value })} data-testid={`hc-trade-${group}-${area}`}><option value="">Select trade…</option>{meta.trades.map((t) => <option key={t}>{t}</option>)}</select>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="card-soft" style={{ padding: 20 }}>
        <div className="lbl-m" style={{ marginBottom: 10 }}>Photos</div>
        <PhotoUploader value={photos} onChange={setPhotos} label="Add health check photos" withAreas />
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button className="btn-outline" disabled={busy} onClick={() => save(false)} data-testid="save-draft-health-btn">Save draft</button>
        <button className="btn-ink" disabled={busy} onClick={() => save(true)} data-testid="save-submit-health-btn">{busy ? "Saving…" : "Save & submit to owner"}</button>
      </div>
    </div>
  );
}
